import React, { useState } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, IconButton, Chip, TextField,
  InputAdornment, Tooltip, Paper, Stack, useTheme,
  Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import { Edit, Delete, Search, Refresh } from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import EmptyState from "../../components/ui/EmptyState";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { ACCOUNT_TYPE_LABELS } from "../../utils/models/accounting/chartOfAccountsModel";

const ChartOfAccountsList = ({ onEdit }) => {
  const theme = useTheme();
  const { data: accounts, loading } = useCollection("chartOfAccounts");
  const [localAccounts, setLocalAccounts] = useState(null);
  const { remove } = useAuditFirestore("chartOfAccounts");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  React.useEffect(() => {
    if (accounts) setLocalAccounts(accounts);
  }, [accounts]);

  const filtered = localAccounts
    ?.filter((a) => a.isActive !== false)
    ?.filter((a) => {
      const matchesSearch =
        search === "" ||
        a.code?.toLowerCase().includes(search.toLowerCase()) ||
        a.name?.toLowerCase().includes(search.toLowerCase());
      const matchesType = !typeFilter || a.type === typeFilter;
      return matchesSearch && matchesType;
    })
    ?.sort((a, b) => (a.code || "").localeCompare(b.code || ""));

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLocalAccounts((prev) => prev?.filter((a) => a.id !== deleteTarget.id));
    const toDelete = deleteTarget;
    setDeleteTarget(null);
    try {
      await remove(toDelete.id);
    } catch (error) {
      console.error("Error deleting:", error);
      setLocalAccounts((prev) => [...prev, toDelete]);
    }
  };

  if (loading || !localAccounts) return <LoadingSpinner />;

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: "20px", border: "1px solid", borderColor: theme.palette.divider }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
          <TextField
            placeholder="Buscar cuenta..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: "100%", sm: 300 }, "& .MuiOutlinedInput-root": { borderRadius: "12px", height: 44 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ width: { xs: "100%", sm: 180 }, height: 44 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} label="Tipo" sx={{ borderRadius: "12px", height: 44 }}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip label={`${filtered?.length || 0} cuentas`} sx={{ height: 44, borderRadius: "12px", fontWeight: 700 }} />
          <Tooltip title="Refrescar">
            <IconButton onClick={() => { if (accounts) setLocalAccounts(accounts); }} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {filtered?.length === 0 ? (
        <EmptyState title="Sin cuentas" description="Añade cuentas al plan contable" />
      ) : (
        <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: theme.palette.divider }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cuenta Padre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((account) => {
                  const parent = filtered.find((a) => a.code === account.parentCode);
                  return (
                    <TableRow key={account.id} hover>
                      <TableCell sx={{ fontWeight: 600, fontFamily: "monospace" }}>{account.code}</TableCell>
                      <TableCell>{account.name}</TableCell>
                      <TableCell>
                        <Chip label={ACCOUNT_TYPE_LABELS[account.type] || account.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{parent ? `${parent.code} - ${parent.name}` : '-'}</TableCell>
                      <TableCell>{account.description || '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => onEdit(account)} color="primary">
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => setDeleteTarget(account)} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar Cuenta"
        message={`¿Eliminar cuenta ${deleteTarget?.code} - ${deleteTarget?.name}?`}
        type="delete"
      />
    </Box>
  );
};

export default ChartOfAccountsList;